import '../css/style.styl'
import 'reset-css'
import 'regenerator-runtime/runtime'

import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'

import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { SMAAPass } from 'three/examples/jsm/postprocessing/SMAAPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { NodePass } from 'three/examples/jsm/nodes/postprocessing/NodePass.js';
import * as Nodes from 'three/examples/jsm/nodes/Nodes.js';
import Stats from 'three/examples/jsm/libs/stats.module.js';
import dat from 'three/examples/jsm/libs/dat.gui.module.js';
import ViewerGUI from './gui'
import TimeChecker from './timeChecker'
import {loadGLTF, loadRGBE, loadFBX, replaceMaterial} from './resourceLoader'

import SceneParameter from './parameter'
import { Vector3 } from 'three'

window.onload = ()=>{
  const app = new App();
  app.init();
}

const INIT_TIME = Date.now();

class App{
  constructor(){
    this.timeChecker = new TimeChecker(INIT_TIME);
    this.sceneParams = new SceneParameter();
  }

  async init(){ 
    // setup renderer
    this.renderer = new THREE.WebGLRenderer( { antialias: true } );
    this.renderer.setPixelRatio( window.devicePixelRatio );
    this.renderer.setSize( window.innerWidth, window.innerHeight );
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = Math.pow( 1.0, 4.0 );
    this.renderer.outputEncoding = THREE.sRGBEncoding;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.info.autoReset = false;
    document.body.appendChild( this.renderer.domElement );


    // create camera
    this.camera = new THREE.PerspectiveCamera( 45, window.innerWidth/window.innerHeight, 0.25, 10000);
    const camPos = new THREE.Vector3(20, 20, 10);
    this.camera.position.set(camPos.x, camPos.y, camPos.z);

    // create scene
    this.scene = new THREE.Scene();

    // render pass (posteffect etc.)
    this.setupRenderPass();
    
    // set up scene objects (wait loading assets before starting rendering)
    await this.initScene();

    // clock
    this.clock = new THREE.Clock();

    // Viewer GUI
    const viewerGUI = new ViewerGUI(this.camera);
    
    // stats
    this.stats = new Stats();
    document.body.appendChild( this.stats.dom );

    // hide infomation
    // this.gui.domElement.style.display = 'none';
    // document.getElementById('info').style.display = 'none'
    // this.stats.dom.style.display = 'none'
      
    // dev tools
    // Observe a scene or a renderer
    if (typeof __THREE_DEVTOOLS__ !== 'undefined') {
      __THREE_DEVTOOLS__.dispatchEvent(new CustomEvent('observe', { detail: this.scene }));
      __THREE_DEVTOOLS__.dispatchEvent(new CustomEvent('observe', { detail: this.renderer }));
    }
    
    // execute render loop
    this.render();
    

    window.addEventListener( 'resize', this.onWindowResize.bind(this), false );

    
  } // end of init()

  onWindowResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();

    this.renderer.setSize( window.innerWidth, window.innerHeight );
    this.composer.setSize( window.innerWidth, window.innerHeight );
  }

  render(){
    const loop = ()=>{
      requestAnimationFrame( loop );

      const delta = this.clock.getDelta();

      this.updateDebugInfo();
      this.nodeFrame.update(delta).updateNode(this.nodepass.material);
      this.composer.render();

    }
    loop();
  }

  updateDebugInfo(){
    // orbit control
    this.controls.update();
    // stats
    this.stats.update();
   
    this.renderer.info.reset();
  }

  async initScene(){
    // scene params
    const shadowBias = -0.00001;

    // put directional light
    this.dirLight = new THREE.DirectionalLight( 0xffffff, 1);
    this.dirLight.position.set( -400, 300, 0 );
    this.dirLight.castShadow = this.sceneParams.bEnableDirLightShadow;
    this.dirLight.shadow.camera.near = 1;
    this.dirLight.shadow.camera.far = 1000;
    this.dirLight.shadow.camera.right = 100;
    this.dirLight.shadow.camera.left = -100;
    this.dirLight.shadow.camera.top	= 100;
    this.dirLight.shadow.camera.bottom = -100;
    this.dirLight.shadow.mapSize.width = 1024;
    this.dirLight.shadow.mapSize.height = 1024;
    this.dirLight.shadow.bias = shadowBias;
    this.dirLight.visible = this.sceneParams.bEnableDirLight;
    this.scene.add(this.dirLight);

    this.ambientLight = new THREE.AmbientLight( 0xffffff );
    this.ambientLight.intensity = this.sceneParams.ambientLightIntensity;
    this.ambientLight.visible = this.sceneParams.bEnableAmbientLight;
    this.scene.add( this.ambientLight);

    // load environment map
    const envData = await loadRGBE(THREE.UnsignedByteType, '../assets/royal_esplanade_1k.hdr');
    const pmremGenerator = new THREE.PMREMGenerator( this.renderer );
    pmremGenerator.compileEquirectangularShader();

    const envMap = pmremGenerator.fromEquirectangular(envData).texture;
    this.scene.background = envMap;
    this.scene.environment = envMap;

    // control
    this.controls = new OrbitControls( this.camera, this.renderer.domElement );
    this.controls.minDistance = 2;
    this.controls.maxDistance = 10000;
    const camTarget = new Vector3(0, 10, 0);
    this.controls.target.set( camTarget.x, camTarget.y, camTarget.z );
    

    envData.dispose();
    pmremGenerator.dispose();
    await this.loadModel(this.sceneParams.modelPath);

    // helper for debug
    this.scene.add( new THREE.CameraHelper( this.dirLight.shadow.camera ) );
  }

  setupRenderPass(){
    // composer
    this.composer = new EffectComposer(this.renderer);

    this.composer.addPass(new RenderPass(this.scene, this.camera));

    // ssao pass
    // this.ssaoPass = new SSAOPass(this.scene, this.camera, window.innerWidth, window.innerHeight);
    // this.ssaoPass.kernelRadius = 16;
    // this.composer.addPass(this.ssaoPass);

    // bloom
    this.bloomPass = new UnrealBloomPass( new THREE.Vector2( window.innerWidth, window.innerHeight ), 1.5, 0.4, 0.85 );
    // this.bloomPass.threshold = Params.bloomParams.bloomThreshold;
    // this.bloomPass.strength = Params.bloomParams.bloomStrength;
    // this.bloomPass.radius = Params.bloomParams.bloomRadius;
    // this.composer.addPass(this.bloomPass);

    // SMAAA
    this.smaaPass = new SMAAPass(window.innerWidth * this.renderer.getPixelRatio(), window.innerHeight * this.renderer.getPixelRatio());
    // this.composer.addPass(this.smaaPass);

    // ColorCorrection
    this.nodeFrame = new Nodes.NodeFrame();
    this.nodepass = new NodePass();
    // this.composer.addPass( this.nodepass );
  }

  
  async loadModel(modelPath){
    const isGltf = modelPath.match(/\.(gltf|glb)$/) != null
    const isFbx = modelPath.match(/\.(fbx)$/) != null

    let model = null;
    if(isGltf){
      const obj = await loadGLTF(modelPath)
      model = obj.scene;
    }else if(isFbx){
      model = await loadFBX(modelPath);
    }else{
      return;
    }
    model.traverse((child)=>{ 
      if(child.isMesh){
        child.material.emissive = 0;
        child.material.envMapIntensity = this.sceneParams.envIntensity;

        if(this.sceneParams.bEnableDirLightShadow){
          if(child.material.opacity < 1){
            child.castShadow = false;
            child.receiveShadow = false;
          }else{
            child.castShadow = true;
            child.receiveShadow = true;
          }
        }

        replaceMaterial(child, "MeshLambertMaterial");
      }
    });

    // add to scene
    this.scene.add(model);  
  }
}

